import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { getErrorMessage } from '~libs/axiosWrapper';
import { deriveFormattedDateRange } from '~support-at-home/lib/derive';

import { ClickableIcon, ConfirmDialog, ErrorResponse, Spinner } from '~components';
import Icon, { faBan, faCalendar, faCircleCheck, faExclamationCircle, faRefresh, faTrash } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import { Claim, ClaimItem } from '~support-at-home/models';

import { claimItemColumnsFactory } from '~support-at-home/forms/claims/claimItemDetailsList';

import { getAll as getServices } from '~appointments/actions/serviceTypes';
import { useGetSahServiceItemsQuery } from '~lookups/actions/sahServiceItems';
import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { useGetSahServiceWrapsQuery } from '~lookups/actions/sahServiceWraps';
import {
  useBulkDeleteClaimItemsMutation,
  useBulkUpdateClaimItemsMutation,
  useDeleteClaimItemMutation,
  useUpdateClaimItemMutation,
  useValidateClaimMutation,
} from '~support-at-home/actions/claims';

import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';

interface ClaimItemsResponse {
  rows: ClaimItem[];
  count: number;
  total: number;
}

interface ComponentProps {
  claim?: Claim;
  claimItems?: ClaimItemsResponse;
  itemsLoading?: boolean;
  claimRefreshing?: boolean;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRefreshClaim: () => void;
}

const EDITABLE_KEYS: (keyof ClaimItem)[] = [
  'sah_wrap_id',
  'sah_item_id',
  'sah_hpt_id',
  'sah_detail_description',
  'sah_hpt_description',
  'is_first_payment_item',
  'is_prescribed_item',
  'purchase_method',
];
const CLAIM_REFRESH_DEBOUNCE_MS = 3000;

const ClaimDetailsEdit: React.FC<ComponentProps> = ({
  claim,
  claimItems,
  itemsLoading = false,
  claimRefreshing = false,
  currentPage,
  pageSize,
  onPageChange,
  onRefreshClaim,
}) => {
  const dispatch = useAppDispatch();
  const [deleteClaimItem] = useDeleteClaimItemMutation();
  const [updateClaimItem] = useUpdateClaimItemMutation();
  const [bulkDeleteClaimItems, { isLoading: isBulkDeleting }] = useBulkDeleteClaimItemsMutation();
  const [bulkUpdateClaimItems, { isLoading: isBulkUpdating }] = useBulkUpdateClaimItemsMutation();
  const [validateClaim, { isLoading: isValidating }] = useValidateClaimMutation();

  const [claimItemsSelected, setClaimItemsSelected] = useState<string[]>([]);
  const [isBulkIgnoreConfirmOpen, setIsBulkIgnoreConfirmOpen] = useState(false);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [mutationError, setMutationError] = useState<any>(null);

  const { data: sahServicesData } = useGetSahServicesQuery();
  const { data: sahServiceWraps } = useGetSahServiceWrapsQuery();
  const { data: sahServiceItemsData } = useGetSahServiceItemsQuery();

  const claimRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAutoValidated = useRef(false);

  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);

  // Early exit guard - component requires a valid claim to function
  if (!claim) {
    return null;
  }

  const claimId = String(claim.id);

  useEffect(() => {
    dispatch(
      getServices({
        deleted: {
          or: [true, false],
        },
      }),
    );
    // Cleanup timer on unmount
    return () => {
      if (claimRefreshTimerRef.current) {
        clearTimeout(claimRefreshTimerRef.current);
      }
    };
  }, []);

  // Auto-validate on first open if claim has never been validated & is not finalised status
  useEffect(() => {
    if (hasAutoValidated.current) return;
    if (!claim.validated_at && claim.status?.tag !== 'finalised' && !claim.is_validating) {
      hasAutoValidated.current = true;
      validateClaim({ id: claimId });
      onRefreshClaim();
    }
  }, [claim]);

  const scheduleDebouncedRefresh = () => {
    // Clear existing timer if any
    if (claimRefreshTimerRef.current) {
      clearTimeout(claimRefreshTimerRef.current);
    }

    // Set new debounced timer to refresh claim statistics
    claimRefreshTimerRef.current = setTimeout(() => {
      onRefreshClaim();
      claimRefreshTimerRef.current = null;
    }, CLAIM_REFRESH_DEBOUNCE_MS);
  };

  const handleChange = async (changedRow: Row) => {
    setMutationError(null);

    const { id, claim_id, ...rest } = changedRow;

    const changes: Partial<ClaimItem> = {};
    for (const key of EDITABLE_KEYS) {
      if (rest[key] !== undefined) {
        changes[key] = rest[key];
      }
    }

    try {
      await updateClaimItem({ id: String(id), claim_id: claimId, changes }).unwrap();
      scheduleDebouncedRefresh();
    } catch (error) {
      setMutationError(error);
    }
  };

  const handleIgnore = async (rowId: string) => {
    setMutationError(null);

    try {
      await updateClaimItem({ id: rowId, claim_id: claimId, changes: { is_ignored: true } }).unwrap();
      scheduleDebouncedRefresh();
    } catch (error) {
      setMutationError(error);
    }
  };

  const handleDelete = async (rowId: string | number) => {
    setMutationError(null);

    try {
      await deleteClaimItem({ id: String(rowId), claim_id: claimId }).unwrap();
      scheduleDebouncedRefresh();
    } catch (error) {
      setMutationError(error);
    }
  };

  const onBulkIgnoreConfirm = async (confirmed: boolean) => {
    setIsBulkIgnoreConfirmOpen(false);
    if (confirmed && claimItemsSelected.length > 0) {
      setMutationError(null);

      try {
        await bulkUpdateClaimItems({
          ids: claimItemsSelected,
          claim_id: claimId,
          changes: { is_ignored: true },
        }).unwrap();
        setClaimItemsSelected([]);
        scheduleDebouncedRefresh();
      } catch (error) {
        setMutationError(error);
      }
    }
  };

  const onBulkDeleteConfirm = async (confirmed: boolean) => {
    setIsBulkDeleteConfirmOpen(false);
    if (confirmed && claimItemsSelected.length > 0) {
      setMutationError(null);

      try {
        await bulkDeleteClaimItems({
          ids: claimItemsSelected,
          claim_id: claimId,
        }).unwrap();

        setClaimItemsSelected([]);
        scheduleDebouncedRefresh();
      } catch (error) {
        setMutationError(error);
      }
    }
  };

  const handleRevalidate = async () => {
    try {
      await validateClaim({ id: claimId });
      // Refetch claim to get is_validating state, which triggers polling
      onRefreshClaim();
    } catch (error) {
      setMutationError(error);
    }
  };

  const columns = claimItemColumnsFactory(
    sahServicesData ?? [],
    serviceTypes ?? [],
    sahServiceWraps ?? [],
    sahServiceItemsData ?? [],
    handleIgnore,
    claim.is_validating,
  );

  const hasStatementItems = claim.items?.some((item) => item.statement?.id) ?? false;
  const readonly = claim.status?.tag === 'finalised' || hasStatementItems;
  const itemsValid = claim.items_valid ?? 0;
  const itemsWithErrors = claim.items_with_errors ?? 0;
  const itemsIgnored = claim.items_ignored ?? 0;

  const renderStats = () => {
    if (claim?.is_validating) {
      return (
        <div className="d-flex align-items-center">
          <Spinner type="clip" className="me-2" loading={true} size={20} />
          <span className="text-muted">Validating...</span>
        </div>
      );
    }

    if (claimRefreshing) {
      return (
        <div className="d-flex align-items-center">
          <Spinner type="clip" className="me-2" loading={true} size={20} />
          <span className="text-muted">Updating...</span>
        </div>
      );
    }

    return [
      { count: itemsWithErrors, label: 'Errors', className: 'text-danger' },
      { count: itemsValid, label: 'Valid', className: 'text-success' },
      { count: itemsIgnored, label: 'Ignored', className: '' },
    ].map(({ count, label, className }) => (
      <div key={label} className="d-flex flex-column pe-3 text-center">
        <span className={`fw-bold ${className}`}>{count}</span>
        <span>{label}</span>
      </div>
    ));
  };

  return (
    <div>
      <ConfirmDialog
        isOpen={isBulkDeleteConfirmOpen}
        messages={
          <span>
            You are about to <b>delete</b> {claimItemsSelected?.length} claim items
          </span>
        }
        onClose={onBulkDeleteConfirm}
      />
      <ConfirmDialog
        isOpen={isBulkIgnoreConfirmOpen}
        messages={
          <span>
            You are about to <b>ignore</b> {claimItemsSelected?.length} claim items
          </span>
        }
        onClose={onBulkIgnoreConfirm}
      />
      <div className="d-flex bg-light rounded p-3 mb-3">
        <div className="d-flex align-items-center">
          <Icon icon={faCircleCheck} className="text-secondary me-3" />

          <div className="d-flex flex-column">
            <h6 className="fw-semibold text-muted mb-1">Claim Details</h6>
            <span className="fw-medium">Review and edit claim lines. Resolve errors before finalising.</span>
          </div>
        </div>

        <div className="d-flex align-items-center ms-auto me-auto">
          <Icon icon={faCalendar} className="text-secondary me-3" />
          <div className="d-flex flex-column">
            <h6 className="fw-semibold text-muted mb-1">Claim Period</h6>
            <span className="fw-medium">
              {deriveFormattedDateRange({ start_date: claim.start_date, end_date: claim.end_date })}
            </span>
          </div>
        </div>

        <div className="d-flex align-items-center ms-auto">
          <div className="d-flex me-2">{renderStats()}</div>
          {!readonly && (
            <div className="d-flex align-items-center ms-auto">
              <ClickableIcon
                id="claim-items-bulk-ignore-button"
                icon={faBan}
                className="mx-2"
                color="muted"
                size="medium"
                disabled={claimItemsSelected.length === 0 || isBulkUpdating || isBulkDeleting}
                onClick={() => setIsBulkIgnoreConfirmOpen(true)}
                toolTip={`Ignore ${claimItemsSelected.length} rows`}
              />
              <ClickableIcon
                id="claim-items-bulk-delete-button"
                icon={faTrash}
                className="mx-2"
                color="danger"
                size="medium"
                disabled={claimItemsSelected.length === 0 || isBulkUpdating || isBulkDeleting}
                onClick={() => setIsBulkDeleteConfirmOpen(true)}
                toolTip={`Delete ${claimItemsSelected.length} rows`}
              />
            </div>
          )}
        </div>
      </div>

      {mutationError && <ErrorResponse message={getErrorMessage(mutationError)} />}

      {itemsWithErrors > 0 && (
        <div className="d-flex align-items-center error-box rounded p-3 mb-3 border-danger border">
          <Icon icon={faExclamationCircle} className="text-danger me-3" />
          <div className="d-flex flex-column">
            <h6 className="fw-semibold text-danger mb-1">{itemsWithErrors} errors found</h6>
            <span className="fw-medium text-danger">Please resolve all errors before completing the claim.</span>
          </div>
          <Spinner type="clip" className="ms-auto me-2" loading={isValidating || claim?.is_validating} size={30} />
          {!isValidating && !claim?.is_validating && !readonly && (
            <ClickableIcon
              id="claims-refresh-validation"
              icon={faRefresh}
              className="ms-auto"
              size="medium"
              color="muted"
              onClick={handleRevalidate}
              toolTip="Refresh Validation"
            />
          )}
        </div>
      )}

      <div className="row mb-3">
        <TableView
          id="claim-items-table"
          columns={columns}
          data={claimItems?.rows ?? []}
          isLoading={itemsLoading}
          emptyMessage="No claim items found for selected filters"
          showAddRow={false}
          allowPagination
          paginationType="pages"
          showCount
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={claimItems?.total ?? 0}
          onPageChange={onPageChange}
          allowSort={false}
          onChangeRow={handleChange}
          onDeleteRow={handleDelete}
          canDeleteRow={(row) => !row.statement?.id}
          isRowDisabled={(row) => row.is_ignored ?? false}
          canSelect={!readonly}
          selectedIds={claimItemsSelected}
          onRowSelectionChange={(selected) => setClaimItemsSelected((selected as (string | number)[]).map(String))}
          readonly={readonly}
          editonly={!readonly}
          compactView
          bordered
        />
      </div>
    </div>
  );
};

export default ClaimDetailsEdit;
